/**
 * High-resolution branded QR codes with the Niko Vibe logo centered.
 * Payload is unchanged — branding is visual only — so existing codes still scan.
 */

import QRCode from 'qrcode';
import { BRAND_LOGO_FALLBACK_SRC } from '../constants/brand';

export const BRANDED_QR_DEFAULTS = {
  size: 1024,
  margin: 3,
  errorCorrectionLevel: 'H',
  logoRatio: 0.24,
  darkColor: '#111827',
  lightColor: '#FFFFFF',
  // JPEG is tiny & reliable for canvas compositing
  logoSrc: BRAND_LOGO_FALLBACK_SRC
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/**
 * @param {string} payload
 * @param {object} [options]
 * @returns {Promise<string>} PNG data URL
 */
export async function generateBrandedQrDataUrl(payload, options = {}) {
  const {
    size = BRANDED_QR_DEFAULTS.size,
    margin = BRANDED_QR_DEFAULTS.margin,
    errorCorrectionLevel = BRANDED_QR_DEFAULTS.errorCorrectionLevel,
    logoRatio = BRANDED_QR_DEFAULTS.logoRatio,
    darkColor = BRANDED_QR_DEFAULTS.darkColor,
    lightColor = BRANDED_QR_DEFAULTS.lightColor,
    logoSrc = BRANDED_QR_DEFAULTS.logoSrc
  } = options;

  const qrDataUrl = await QRCode.toDataURL(String(payload), {
    width: size,
    margin,
    errorCorrectionLevel,
    color: { dark: darkColor, light: lightColor }
  });

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return qrDataUrl;
  }

  const qrImg = await loadImage(qrDataUrl);
  ctx.drawImage(qrImg, 0, 0, size, size);

  try {
    const logo = await loadImage(logoSrc);
    const logoMaxW = size * logoRatio;
    const logoMaxH = size * logoRatio * 0.55;
    const scale = Math.min(logoMaxW / logo.naturalWidth, logoMaxH / logo.naturalHeight);
    const lw = logo.naturalWidth * scale;
    const lh = logo.naturalHeight * scale;
    const pad = Math.max(10, size * 0.018);
    const boxW = lw + pad * 2;
    const boxH = lh + pad * 2;
    const bx = (size - boxW) / 2;
    const by = (size - boxH) / 2;

    roundRect(ctx, bx, by, boxW, boxH, Math.min(boxW, boxH) * 0.18);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = '#F3F4F6';
    ctx.lineWidth = Math.max(1, size * 0.002);
    ctx.stroke();

    ctx.drawImage(logo, bx + pad, by + pad, lw, lh);
  } catch (err) {
    console.warn('Branded QR: logo overlay skipped', err);
  }

  return canvas.toDataURL('image/png');
}

/** Marketing / print downloads */
export const QR_SIZE_DOWNLOAD = 1280;
/** On-screen ticket / event display (still sharp when CSS-scaled) */
export const QR_SIZE_DISPLAY = 720;
